import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Empty, Field, SectionTitle, Table } from "./_ui";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";

export default function SettingsView() {
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    const [s, st] = await Promise.all([api.get("/admin/settings"), api.get("/admin/staff")]);
    setSettings(s.data);
    setStaff(st.data);
  };
  useEffect(() => {
    load();
  }, []);

  if (!settings)
    return (
      <p className="text-sm text-slate-500">
        <Loader2 size={14} className="inline animate-spin mr-2" />
        Chargement…
      </p>
    );

  const pricing = {
    ...settings.pricing,
    recurring: settings.pricing.recurring || settings.pricing.standard || { under100: 4, over100: 2 },
    oneoff: settings.pricing.oneoff || { under100: 5, over100: 3 },
  };

  const updatePricing = (path, value) => {
    const next = JSON.parse(JSON.stringify(settings));
    next.pricing.recurring = next.pricing.recurring || next.pricing.standard || { under100: 4, over100: 2 };
    next.pricing.oneoff = next.pricing.oneoff || { under100: 5, over100: 3 };
    const segs = path.split(".");
    let cur = next.pricing;
    for (let i = 0; i < segs.length - 1; i++) cur = cur[segs[i]];
    cur[segs[segs.length - 1]] = value;
    setSettings(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", {
        pricing: settings.pricing,
      });
      setSettings(r.data);
      setSavedAt(new Date().toLocaleTimeString("fr-CH"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-view">
      {/* Pricing */}
      <div>
        <SectionTitle
          action={
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary !text-xs !py-2 !px-3"
              data-testid="settings-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Enregistrer
              {savedAt && <span className="ml-2 opacity-70">· {savedAt}</span>}
            </button>
          }
        >
          Tarification
        </SectionTitle>
        <div className="grid sm:grid-cols-2 gap-5 bg-white border border-slate-200 rounded-md p-5">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">
              Passages réguliers (CHF / m² / intervention)
            </p>
            <Field label="Surface < 100 m²">
              <input
                className="field"
                type="number"
                step="0.1"
                value={pricing.recurring.under100}
                onChange={(e) =>
                  updatePricing("recurring.under100", Number(e.target.value))
                }
                data-testid="set-rec-under100"
              />
            </Field>
            <Field label="Surface ≥ 100 m²">
              <input
                className="field"
                type="number"
                step="0.1"
                value={pricing.recurring.over100}
                onChange={(e) =>
                  updatePricing("recurring.over100", Number(e.target.value))
                }
                data-testid="set-rec-over100"
              />
            </Field>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">
              Intervention ponctuelle (CHF / m²)
            </p>
            <Field label="Surface < 100 m²">
              <input
                className="field"
                type="number"
                step="0.1"
                value={pricing.oneoff.under100}
                onChange={(e) => updatePricing("oneoff.under100", Number(e.target.value))}
                data-testid="set-oneoff-under100"
              />
            </Field>
            <Field label="Surface ≥ 100 m²">
              <input
                className="field"
                type="number"
                step="0.1"
                value={pricing.oneoff.over100}
                onChange={(e) => updatePricing("oneoff.over100", Number(e.target.value))}
                data-testid="set-oneoff-over100"
              />
            </Field>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">
              Promotion 1ère intervention
            </p>
            <Field label="Activée">
              <select
                className="field"
                value={settings.pricing.firstPromo.enabled ? "yes" : "no"}
                onChange={(e) =>
                  updatePricing("firstPromo.enabled", e.target.value === "yes")
                }
                data-testid="set-promo-enabled"
              >
                <option value="no">Désactivée</option>
                <option value="yes">Activée</option>
              </select>
            </Field>
            <Field label="Type">
              <select
                className="field"
                value={settings.pricing.firstPromo.type}
                onChange={(e) => updatePricing("firstPromo.type", e.target.value)}
                data-testid="set-promo-type"
              >
                <option value="percent">Pourcentage (%)</option>
                <option value="fixed">Montant fixe (CHF)</option>
              </select>
            </Field>
            <Field label="Valeur">
              <input
                className="field"
                type="number"
                step="0.1"
                value={settings.pricing.firstPromo.value}
                onChange={(e) =>
                  updatePricing("firstPromo.value", Number(e.target.value))
                }
                data-testid="set-promo-value"
              />
            </Field>
          </div>
        </div>
      </div>

      {/* Staff */}
      <StaffSection staff={staff} reload={load} />
    </div>
  );
}

function StaffSection({ staff, reload }) {
  const [editing, setEditing] = useState(null); // staff obj or 'new'
  return (
    <div>
      <SectionTitle
        action={
          <button
            onClick={() => setEditing({ name: "", phone: "", whatsapp: "", region: "", active: true })}
            className="btn-primary !text-xs !py-2 !px-3"
            data-testid="staff-add-btn"
          >
            <Plus size={14} /> Ajouter
          </button>
        }
      >
        Personnel
      </SectionTitle>
      {staff.length === 0 ? (
        <Empty label="Aucun personnel. Ajoutez un membre de l'équipe." />
      ) : (
        <Table headers={["Nom", "Téléphone", "WhatsApp", "Région", "Actif", ""]} testid="staff-table">
          {staff.map((s) => (
            <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="p-3 font-medium text-slate-900">{s.name}</td>
              <td className="p-3 text-slate-700">{s.phone || "—"}</td>
              <td className="p-3 text-slate-700">{s.whatsapp || "—"}</td>
              <td className="p-3 text-slate-700">{s.region || "—"}</td>
              <td className="p-3">{s.active ? "Oui" : "Non"}</td>
              <td className="p-3 text-right">
                <button
                  onClick={() => setEditing(s)}
                  className="text-xs text-[#016C36] hover:underline font-semibold"
                  data-testid={`staff-edit-${s.id}`}
                >
                  Modifier
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {editing && (
        <StaffEditModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function StaffEditModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/staff", form);
      else await api.patch(`/admin/staff/${item.id}`, form);
      onSaved();
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer ${item.name} ?`)) return;
    await api.delete(`/admin/staff/${item.id}`);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full rounded-md p-6" onClick={(e) => e.stopPropagation()} data-testid="staff-modal">
        <h3 className="font-display font-bold text-lg mb-4">
          {isNew ? "Nouveau membre" : `Modifier ${item.name}`}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom">
            <input className="field" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
          </Field>
          <Field label="Téléphone">
            <input className="field" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone" />
          </Field>
          <Field label="WhatsApp">
            <input className="field" value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="staff-whatsapp" />
          </Field>
          <Field label="Région">
            <input className="field" value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} />
          </Field>
          <Field label="Actif">
            <select className="field" value={form.active ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })}>
              <option value="yes">Oui</option>
              <option value="no">Non</option>
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Notes">
              <textarea className="field" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
        </div>
        <div className="flex justify-between mt-5">
          {!isNew && (
            <button onClick={remove} className="btn-secondary !text-xs text-[#016C36]" data-testid="staff-delete">
              <Trash2 size={12} /> Supprimer
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="btn-secondary !text-xs">
              Annuler
            </button>
            <button onClick={save} disabled={saving || !form.name} className="btn-primary !text-xs" data-testid="staff-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
